/**
 * Verklein een afbeeldingsbestand client-side tot max `maxSize` px op de
 * langste zijde, als JPEG met gegeven quality. Dit ene bestand dient als
 * thumbnail, vergroting én download — het origineel wordt niet geüpload, wat
 * server-ruimte bespaart. PDF's worden ongewijzigd teruggegeven (geen resize).
 */
export async function resizeImage(
  file: File,
  maxSize = 720,
  quality = 0.82,
): Promise<File> {
  // PDF's en niet-afbeeldingen niet aanraken.
  if (file.type === "application/pdf" || !file.type.startsWith("image/")) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    const maxDim = Math.max(width, height);
    const scale = maxDim > maxSize ? maxSize / maxDim : 1;
    const targetW = Math.round(width * scale);
    const targetH = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", quality),
    );
    bitmap.close?.();
    if (!blob) return file;

    const originalName = file.name.replace(/\.[^.]+$/, "");
    return new File([blob], `${originalName}.jpg`, { type: "image/jpeg" });
  } catch {
    // Als canvas-conversie faalt (bijv. exotisch formaat), retourneer origineel.
    return file;
  }
}